<script setup>
// FLOW-11 (SPEC S4, R9 e Q5; PLAN G9, Task 4.7) — o cofre da conta, de onde o `secret('nome')` de um
// flow da conta resolve.
//
// FLOW-13 (SPEC S1, Q1, Q5; PLAN G3, G4, G14, G19; Task 5.1) — o mesmo componente em dois lugares: a
// sub-aba "Segredos" da aba Ferramentas de todo agente (sem título, porque a sub-aba já diz o nome) e
// a aba Segredos de Configurações › PowerTools (com título). O cofre é um só por conta, e serve às
// ferramentas e aos flows, e também às tools antigas, que passam a achar o segredo aqui antes do
// agente (R2).
//
// **O valor nunca reaparece.** Ele é digitado num campo de senha, vai à API e some: a lista mostra o
// nome, quem gravou e quando, e "Usado por". "Trocar valor" grava um valor novo sem mostrar o antigo
// (a API nem o devolve), e fechar o diálogo esquece o que foi digitado. O "Novo segredo" é o
// `NovoSegredo`, o mesmo da caixa do passo `http`.
//
// **"Usado por" (R7)** vem calculado pelo servidor, só com nomes, e abre a lista das ferramentas e dos
// flows que leem o segredo, cada um com o agente. A mesma lista vai na confirmação de "Excluir" e, só
// com as tools antigas, no aviso de "Trocar valor": elas só leem o valor novo depois que o agente é
// publicado de novo.
//
// **Quem gravou:** o segredo que a migração copiou de um agente (S3) diz de qual ("Copiado de Sayuri
// na migração", `copiado_de`, §6 do PLAN, item 6); o resto diz quem gravou por último. O campo é lido
// com tolerância: o servidor de antes da migração não o manda.
//
// **Abaixo do cofre, só para o super-admin:** os nomes que ficaram nos agentes (S3, Q3; §6 do PLAN,
// item 5; tela 5 do mockup), com "Criar na conta" na colisão (`FicaramNosAgentes`). A falha dessa
// lista não derruba o cofre.
import { ref, computed, onMounted } from 'vue';
import { useI18n, I18nT } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import flowSecretsApi from 'dashboard/api/flowSecrets';
import { normalizar } from '../powerAgents/busca';
import NovoSegredo from './NovoSegredo.vue';
import ListaDeUso from './ListaDeUso.vue';
import FicaramNosAgentes from './FicaramNosAgentes.vue';
import {
  ferramentasDoUso,
  legadasDoUso,
  agentesDasLegadas,
  listaDeNomes,
} from './usoDoSegredo';

defineProps({
  // Em Configurações › PowerTools a aba leva o título; na sub-aba do agente, não.
  comTitulo: { type: Boolean, default: false },
});

// Numa linha só no template: quebrado, o `{{ }}` ganha espaço dentro do <code>.
const CLASSE_DO_CODIGO = 'px-1 font-mono text-sm rounded-md bg-n-alpha-black2';

const { t, locale } = useI18n();
const store = useStore();
const { isSuperAdmin } = useAdmin();
const accountId = computed(() => store.getters.getCurrentAccountId);

const segredos = ref([]);
const carregando = ref(true);
const erroAoCarregar = ref(false);
const salvando = ref(false);
const busca = ref('');
const abertos = ref([]);
const ficaram = ref([]);

const novoSegredo = ref(null);
const dialogoTrocar = ref(null);
const dialogoExcluir = ref(null);
const valor = ref('');
const alvo = ref(null);

const emOrdem = lista =>
  [...lista].sort((a, b) => a.name.localeCompare(b.name));

// Os nomes que ficaram nos agentes: só o super-admin os pede (o servidor recusa os outros com 403).
async function carregarFicaram() {
  if (!isSuperAdmin.value) return;
  try {
    ficaram.value = await flowSecretsApi.ficaramNosAgentes(accountId.value);
  } catch (e) {
    ficaram.value = [];
  }
}

// `silencioso`: depois de criar, a lista volta com o "Usado por" do servidor (um flow pode já citar o
// nome que acabou de nascer) sem piscar o "Carregando".
async function carregar({ silencioso = false } = {}) {
  if (!silencioso) {
    carregando.value = true;
    erroAoCarregar.value = false;
  }
  try {
    segredos.value = emOrdem(await flowSecretsApi.index(accountId.value));
    erroAoCarregar.value = false;
  } catch (e) {
    if (!silencioso) erroAoCarregar.value = true;
  } finally {
    carregando.value = false;
  }
}

const contagem = computed(() =>
  t('POWER_TOOLS.SECRETS.COUNT', { count: segredos.value.length })
);
const nomes = computed(() => segredos.value.map(segredo => segredo.name));

// -- Busca: pelo nome, sem diferença de maiúscula e acento (o `normalizar` da busca do agente).
const termo = computed(() => normalizar(busca.value).trim());
const visiveis = computed(() =>
  termo.value
    ? segredos.value.filter(segredo =>
        normalizar(segredo.name).includes(termo.value)
      )
    : segredos.value
);

// -- "Usado por"
const usoDe = segredo => ferramentasDoUso(segredo.usado_por);
const estaAberto = segredo => abertos.value.includes(segredo.id);
function alternarUso(segredo) {
  abertos.value = estaAberto(segredo)
    ? abertos.value.filter(id => id !== segredo.id)
    : [...abertos.value, segredo.id];
}

// -- Quem gravou e quando
const formatoDaData = computed(
  () =>
    new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
      dateStyle: 'short',
      timeStyle: 'short',
    })
);
const quando = segredo =>
  segredo.gravado_em
    ? formatoDaData.value.format(new Date(segredo.gravado_em))
    : '';
const nomeDaOrigem = segredo => {
  const origem = segredo.copiado_de;
  return origem && typeof origem.nome === 'string' && origem.nome
    ? origem.nome
    : null;
};
function quemGravou(segredo) {
  const origem = nomeDaOrigem(segredo);
  if (origem) return t('POWER_TOOLS.SECRETS.COPIED_FROM', { name: origem });
  return segredo.gravado_por
    ? t('POWER_TOOLS.SECRETS.SAVED_BY', { name: segredo.gravado_por.name })
    : t('POWER_TOOLS.SECRETS.SAVED_BY_UNKNOWN');
}

// -- Novo segredo (o mesmo diálogo da caixa do `http` e do "Criar segredo" do compilador)
function abrirNovo(nome = '') {
  novoSegredo.value?.abrir(nome);
}

async function aoCriar(criado) {
  if (criado?.name) {
    segredos.value = emOrdem([
      ...segredos.value.filter(segredo => segredo.name !== criado.name),
      { usado_por: [], ...criado },
    ]);
  }
  // Criar na conta tira o nome da colisão: a lista volta do servidor junto com o cofre.
  await Promise.all([carregar({ silencioso: true }), carregarFicaram()]);
}

// -- Trocar valor e Excluir
const usoDoAlvo = computed(() => (alvo.value ? usoDe(alvo.value) : []));
const legadasDoAlvo = computed(() =>
  alvo.value ? legadasDoUso(alvo.value.usado_por) : []
);
const agentesAPublicar = computed(() =>
  listaDeNomes(
    alvo.value ? agentesDasLegadas(alvo.value.usado_por) : [],
    locale.value
  )
);

// O valor digitado não sobrevive ao diálogo: salvo ou fechado, ele some.
function esquecerValor() {
  valor.value = '';
}

function abrirTrocar(segredo) {
  alvo.value = segredo;
  esquecerValor();
  dialogoTrocar.value?.open();
}

function abrirExcluir(segredo) {
  alvo.value = segredo;
  dialogoExcluir.value?.open();
}

// Erro conhecido do servidor vira frase; o resto, a frase da ação. Nunca o texto cru da resposta.
function avisarFalha(erro, fraseDaAcao) {
  const semCifra = erro?.response?.data?.error === 'cofre_sem_cifra';
  useAlert(semCifra ? t('POWER_TOOLS.SECRETS.NO_CIPHER') : fraseDaAcao);
}

async function trocar() {
  if (!alvo.value || valor.value === '') return;
  salvando.value = true;
  try {
    const atualizado = await flowSecretsApi.update(
      accountId.value,
      alvo.value.id,
      { secret: valor.value }
    );
    // A resposta do PATCH não traz o "Usado por", que não muda ao trocar o valor. A origem da
    // migração vem só dela: depois de trocado, o valor já não é a cópia.
    segredos.value = segredos.value.map(segredo =>
      segredo.id === atualizado.id
        ? { ...atualizado, usado_por: segredo.usado_por }
        : segredo
    );
    esquecerValor();
    dialogoTrocar.value?.close();
    useAlert(t('POWER_TOOLS.SECRETS.CHANGED'));
  } catch (erro) {
    avisarFalha(erro, t('POWER_TOOLS.SECRETS.SAVE_ERROR'));
  } finally {
    salvando.value = false;
  }
}

async function excluir() {
  if (!alvo.value) return;
  salvando.value = true;
  try {
    await flowSecretsApi.destroy(accountId.value, alvo.value.id);
    segredos.value = segredos.value.filter(
      segredo => segredo.id !== alvo.value.id
    );
    dialogoExcluir.value?.close();
    useAlert(t('POWER_TOOLS.SECRETS.DELETED'));
  } catch (erro) {
    avisarFalha(erro, t('POWER_TOOLS.SECRETS.DELETE_ERROR'));
  } finally {
    salvando.value = false;
  }
}

onMounted(() => {
  carregar();
  carregarFicaram();
});
</script>

<template>
  <section class="flex flex-col gap-4" data-segredos>
    <header class="flex flex-col gap-1 max-w-3xl">
      <h2 v-if="comTitulo" class="mb-0 text-heading-1 text-n-slate-12">
        {{ t('POWER_TOOLS.SECRETS.TITLE') }}
      </h2>
      <p class="mb-0 text-sm text-n-slate-11" data-descricao-do-cofre>
        {{ t('POWER_TOOLS.SECRETS.DESCRIPTION') }}
      </p>
    </header>

    <p v-if="carregando" class="py-6 mb-0 text-sm text-center text-n-slate-11">
      {{ t('POWER_TOOLS.SECRETS.LOADING') }}
    </p>

    <div
      v-else-if="erroAoCarregar"
      class="flex flex-col items-center gap-3 py-6 text-center"
    >
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('POWER_TOOLS.SECRETS.LOAD_ERROR') }}
      </p>
      <Button
        size="sm"
        variant="faded"
        color="slate"
        data-tentar-de-novo
        :label="t('POWER_TOOLS.SECRETS.RETRY')"
        @click="carregar()"
      />
    </div>

    <template v-else-if="segredos.length">
      <div class="flex items-center justify-between gap-3">
        <span class="text-xs font-medium text-n-slate-11">{{ contagem }}</span>
        <Button
          size="sm"
          icon="i-lucide-plus"
          data-novo-segredo
          :label="t('POWER_TOOLS.SECRETS.NEW')"
          @click="abrirNovo()"
        />
      </div>

      <!-- A lupa entra pelo slot `prefix`, como na busca das ferramentas: o `Input` já nasce com
           `reset-base`, e o recuo do texto abre lugar para ela. -->
      <Input
        v-model="busca"
        data-busca-de-segredo
        size="sm"
        class="w-full"
        autocomplete="off"
        spellcheck="false"
        custom-input-class="ltr:!pl-8 rtl:!pr-8"
        :placeholder="t('POWER_TOOLS.SECRETS.SEARCH_PLACEHOLDER')"
      >
        <template #prefix>
          <span
            class="absolute z-10 top-2 i-lucide-search size-4 text-n-slate-10 ltr:left-2.5 rtl:right-2.5"
            aria-hidden="true"
          />
        </template>
      </Input>

      <ul
        v-if="visiveis.length"
        class="p-0 m-0 list-none border divide-y rounded-pz border-n-weak divide-n-weak bg-n-card shadow-pz"
      >
        <li
          v-for="segredo in visiveis"
          :key="segredo.id"
          class="flex flex-col"
          data-segredo
        >
          <div
            class="flex flex-wrap items-center gap-x-3.5 gap-y-2 py-3.5 pe-4 ps-5"
          >
            <div class="flex flex-col flex-1 min-w-0 gap-0.5">
              <span
                class="font-mono text-sm font-medium truncate text-n-slate-12"
                data-nome-do-segredo
              >
                {{ segredo.name }}
              </span>
              <span
                class="flex flex-wrap items-center gap-1.5 text-xs text-n-slate-11"
              >
                <span data-quem-gravou>{{ quemGravou(segredo) }}</span>
                <span
                  class="rounded-full size-[3px] bg-n-slate-10"
                  aria-hidden="true"
                />
                <time :datetime="segredo.gravado_em">{{
                  quando(segredo)
                }}</time>
              </span>
            </div>

            <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
              <button
                v-if="usoDe(segredo).length"
                type="button"
                class="inline-flex items-center h-7 gap-1 px-2.5 py-0 text-xs rounded-lg transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-n-brand"
                :class="
                  estaAberto(segredo)
                    ? 'bg-n-alpha-2 text-n-slate-12'
                    : 'bg-transparent text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
                "
                :aria-expanded="estaAberto(segredo) ? 'true' : 'false'"
                :aria-controls="`uso-do-segredo-${segredo.id}`"
                data-usado-por
                @click="alternarUso(segredo)"
              >
                {{
                  t(
                    'POWER_TOOLS.SECRETS.USED_BY',
                    { count: usoDe(segredo).length },
                    usoDe(segredo).length
                  )
                }}
                <span
                  class="size-3.5 text-n-slate-10"
                  :class="
                    estaAberto(segredo)
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                  aria-hidden="true"
                />
              </button>
              <span v-else class="px-2.5 text-xs text-n-slate-10" data-sem-uso>
                {{ t('POWER_TOOLS.SECRETS.UNUSED') }}
              </span>

              <!-- Status é glifo colorido com texto neutro, nunca etiqueta de fundo colorido. -->
              <span
                class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-11"
                data-estado
              >
                <span
                  class="i-lucide-lock size-3.5 text-n-teal-9"
                  aria-hidden="true"
                />
                {{ t('POWER_TOOLS.SECRETS.STATUS') }}
              </span>

              <div class="flex items-center gap-1">
                <Button
                  size="xs"
                  variant="faded"
                  color="slate"
                  data-trocar
                  :label="t('POWER_TOOLS.SECRETS.CHANGE')"
                  @click="abrirTrocar(segredo)"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  color="ruby"
                  data-excluir
                  :label="t('POWER_TOOLS.SECRETS.DELETE')"
                  @click="abrirExcluir(segredo)"
                />
              </div>
            </div>
          </div>

          <div
            v-if="estaAberto(segredo)"
            :id="`uso-do-segredo-${segredo.id}`"
            class="px-3 pb-2.5"
            data-uso-aberto
          >
            <ListaDeUso :ferramentas="usoDe(segredo)" />
          </div>
        </li>
      </ul>

      <div
        v-else
        class="flex flex-col items-center gap-1.5 px-4 py-7 text-center border rounded-pz border-n-weak bg-n-card shadow-pz"
        data-busca-sem-resultado
      >
        <span
          class="i-lucide-search size-4 text-n-slate-10"
          aria-hidden="true"
        />
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('POWER_TOOLS.SECRETS.SEARCH_EMPTY', { termo: busca.trim() }) }}
        </p>
      </div>
    </template>

    <div
      v-else
      class="flex flex-col items-center gap-3 px-6 py-12 text-center border rounded-pz border-n-weak bg-n-card shadow-pz"
      data-segredos-vazio
    >
      <span
        class="grid size-10 place-items-center rounded-xl bg-n-alpha-black2 text-n-slate-11"
      >
        <span class="i-lucide-key-round size-4" aria-hidden="true" />
      </span>
      <h3 class="mb-0 text-heading-2 text-n-slate-12">
        {{ t('POWER_TOOLS.SECRETS.EMPTY_TITLE') }}
      </h3>
      <p class="mb-0 text-sm max-w-prose text-n-slate-11">
        {{ t('POWER_TOOLS.SECRETS.EMPTY_DESCRIPTION') }}
      </p>
      <Button
        data-novo-segredo
        :label="t('POWER_TOOLS.SECRETS.NEW')"
        @click="abrirNovo()"
      />
    </div>

    <FicaramNosAgentes
      v-if="isSuperAdmin && ficaram.length && !carregando"
      class="mt-4"
      :itens="ficaram"
      @criar-na-conta="abrirNovo"
    />

    <NovoSegredo
      ref="novoSegredo"
      :nomes-existentes="nomes"
      @criado="aoCriar"
    />

    <Dialog
      ref="dialogoTrocar"
      :title="t('POWER_TOOLS.SECRETS.CHANGE_TITLE', { name: alvo?.name })"
      :description="t('POWER_TOOLS.SECRETS.CHANGE_DESCRIPTION')"
      :confirm-button-label="t('POWER_TOOLS.SECRETS.CHANGE')"
      :disable-confirm-button="valor === ''"
      :is-loading="salvando"
      @confirm="trocar"
      @close="esquecerValor"
    >
      <div class="flex flex-col gap-4">
        <div data-valor-novo>
          <Input
            v-model="valor"
            type="password"
            :label="t('POWER_TOOLS.SECRETS.NEW_VALUE_LABEL')"
            autofocus
            :placeholder="t('POWER_TOOLS.SECRETS.NEW_VALUE_PLACEHOLDER')"
            autocomplete="new-password"
            spellcheck="false"
          />
        </div>
        <!-- As tools antigas leem o valor na publicação do agente: só elas pedem publicar de novo. -->
        <div
          v-if="legadasDoAlvo.length"
          class="flex items-start gap-2.5 px-3 py-2.5 border rounded-lg border-n-weak"
          data-aviso-das-legadas
        >
          <span
            class="i-lucide-triangle-alert size-4 mt-0.5 shrink-0 text-n-amber-11"
            aria-hidden="true"
          />
          <div class="flex flex-col flex-1 min-w-0 gap-2">
            <p class="mb-0 text-sm text-n-slate-12">
              {{
                t('POWER_TOOLS.SECRETS.CHANGE_LEGACY', {
                  agentes: agentesAPublicar,
                })
              }}
            </p>
            <ListaDeUso com-borda :ferramentas="legadasDoAlvo" />
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="dialogoExcluir"
      type="alert"
      :title="t('POWER_TOOLS.SECRETS.DELETE_TITLE', { name: alvo?.name })"
      :confirm-button-label="t('POWER_TOOLS.SECRETS.DELETE')"
      :is-loading="salvando"
      @confirm="excluir"
    >
      <template #description>
        <I18nT
          v-if="usoDoAlvo.length"
          keypath="POWER_TOOLS.SECRETS.DELETE_DESCRIPTION"
          :plural="usoDoAlvo.length"
          tag="p"
          class="mb-0 text-sm text-n-slate-11"
          data-descricao
        >
          <template #count>{{ usoDoAlvo.length }}</template>
          <template #name>
            <code :class="CLASSE_DO_CODIGO">{{ alvo?.name }}</code>
          </template>
        </I18nT>
        <I18nT
          v-else
          keypath="POWER_TOOLS.SECRETS.DELETE_DESCRIPTION_UNUSED"
          tag="p"
          class="mb-0 text-sm text-n-slate-11"
          data-descricao
        >
          <template #name>
            <code :class="CLASSE_DO_CODIGO">{{ alvo?.name }}</code>
          </template>
        </I18nT>
      </template>
      <ListaDeUso v-if="usoDoAlvo.length" com-borda :ferramentas="usoDoAlvo" />
    </Dialog>
  </section>
</template>
